import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import FormControl, { TextFieldControl } from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import LoadingButton from "components/LoadingButton";
import Row from "components/Form/Row";
import Loader from "components/Layout/Loader";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";

import {
  compose,
  fileSizeValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";

import {
  Container,
  TitleContainer,
  Buttons,
  ButtonContainer,
  UploadPreviewFieldControl,
} from "./styles";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import AnswerOptionStore from "stores/AnswerOptionStore";
import TagDictionary from "stores/dictionary/TagDictionary";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
  initialValues?: any;
  isFormLoading?: boolean;
};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class OptionForm extends React.Component<Props> {
  @observable updateConfirmationIsOpened: boolean = false;

  get isEditMode() {
    const { initialValues } = this.props;
    return !!initialValues;
  }

  get formTitle() {
    return !this.isEditMode ? "Add Option" : "Edit Option";
  }

  @computed get initialValues() {
    return this.isEditMode ? AnswerOptionStore.details?.formData : {};
  }

  @computed get publishStatus() {
    const publishStatus = AnswerOptionStore.details?.formData?.publishStatus;

    return publishStatus;
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  componentDidMount() {
    const { initialValues } = this.props;

    Promise.all([TagDictionary.fetch()]);

    if (initialValues?.value?.id) {
      AnswerOptionStore.fetchOptionDetails(initialValues?.value?.id);
    }
  }

  @computed get displayValues(): any {
    const formData = AnswerOptionStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get tagOptions() {
    return [...TagDictionary.options];
  }

  componentWillUnmount() {
    AnswerOptionStore.resetOptionsDetails();
  }

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    const { onSubmit } = this.props;

    this.closeUpdateConfirmation();
    onSubmit(values);
  };

  onSubmit = (values: any) => {
    const { onSubmit } = this.props;

    onSubmit(values);
  };

  render() {
    const { onClose, isFormLoading, onChangeIsDirty } = this.props;

    return (
      <FormControl onSubmit={this.onSubmit} initialValues={this.initialValues}>
        {this.updateConfirmationIsOpened && (
          <FormSpy subscription={{ values: true }}>
            {({ values }) => (
              <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                <ConfirmationDialog
                  title="Save Changes to Production"
                  text="All changes made will be published to production. Are you sure you want to proceed?"
                  acceptText="Proceed"
                  declineText="Cancel"
                  onAccept={this.confirmUpdate(values)}
                  onDecline={this.closeUpdateConfirmation}
                />
              </Popup>
            )}
          </FormSpy>
        )}
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <TitleContainer>
            <Typography variant="h1" color="textPrimary">
              {this.formTitle}
            </Typography>
            {this.isEditMode && this.publishStatus && (
              <PublishStatus status={this.publishStatus} />
            )}
          </TitleContainer>

          {!isFormLoading ? (
            <>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Title"
                  name="title"
                  placeholder="Enter title"
                  validate={lengthValidator(128)}
                  fullWidth
                  required
                />
              </Row>
              <Row>
                <TextFieldControl
                  variant="outlined"
                  margin="none"
                  label="Code"
                  name="code"
                  placeholder="Enter code"
                  validate={lengthValidator(32)}
                  fullWidth
                  required
                />
              </Row>
              <Row>
                <DropdownFieldControl
                  name="tag"
                  options={this.tagOptions}
                  label="Tag"
                  multi
                  isClearable
                />
              </Row>
              <Row>
                <UploadPreviewFieldControl
                  title="Icon"
                  variant="outlined"
                  margin="none"
                  name="file"
                  acceptedFiles={ACCEPTED_IMAGE_TYPES}
                  validate={compose(
                    this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                    fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                  )}
                  required={false}
                />
              </Row>
            </>
          ) : (
            <Row>
              <Loader />
            </Row>
          )}
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      {!this.isEditMode ? "Add" : "Save"}
    </LoadingButton>
  );
}

export default OptionForm;
